import React, { useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import AppContext from './../../../contexts/AppContext';

type AddressComponents = {
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};

export type AddressInfo = {
  components: AddressComponents;
  fullAddress: string;
  googleFormattedAddress: string;
  isValid: boolean;
};

interface GoogleAddressProps {
  onAddressSelected: (address: AddressInfo) => void;
  selectedCountry: string;
  defaultValue?: string | undefined;
}

const GoogleAddress: React.FC<GoogleAddressProps> = ({
  onAddressSelected,
  selectedCountry,
  defaultValue,
}) => {
  const { setGoogleAPILoaded } = useContext(AppContext);
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [place, setPlace] = useState<string | null | undefined>(
    defaultValue || null
  );
  const [isTouched, setIsTouched] = useState(defaultValue ? true : false);
  const [isValid, setIsValid] = useState(defaultValue ? true : false);

  const onLoad = (autoC: google.maps.places.Autocomplete | null) =>
    setAutocomplete(autoC);

  const inputRef: any = useRef(null);

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      const formattedAddress = place.formatted_address;
      const streetNumber = getStreetNumber(place);
      const streetName = getStreetName(place);
      const city = getCity(place);
      const state = getState(place);
      const country = getCountry(place);
      const postalCode = getPostalCode(place);

      setPlace(formattedAddress);

      const isValidAddress = !!(
        streetNumber &&
        streetName &&
        city &&
        country &&
        postalCode &&
        state
      );

      setIsValid(isValidAddress);

      const addressInfo = {
        components: {
          street: `${streetNumber + ' '}${streetName}`,
          city,
          state,
          country,
          postalCode,
        },
        fullAddress: formattedAddress!,
        googleFormattedAddress: formattedAddress!,
        isValid: isValidAddress,
      };
      onAddressSelected(addressInfo);
    }
  };

  function getStreetName(
    place: google.maps.places.PlaceResult | undefined
  ): string {
    if (place && place.address_components) {
      for (let component of place.address_components) {
        if (component.types.includes('route')) {
          return component.short_name;
        }
      }
    }
    return '';
  }

  function getStreetNumber(
    place: google.maps.places.PlaceResult | undefined
  ): string {
    if (place && place.address_components) {
      for (let component of place.address_components) {
        if (component.types.includes('street_number')) {
          return component.long_name;
        }
      }
    }
    return '';
  }

  function getCity(place: google.maps.places.PlaceResult | undefined): string {
    if (place && place.address_components) {
      const cityComponents = [
        'locality',
        'administrative_area_level_2',
        'administrative_area_level_1',
      ];

      for (let component of place.address_components) {
        for (let type of cityComponents) {
          if (component.types.includes(type)) {
            return component.long_name;
          }
        }
      }
    }
    return '';
  }

  function getState(place: google.maps.places.PlaceResult | undefined): string {
    if (place && place.address_components) {
      for (let component of place.address_components) {
        if (component.types.includes('administrative_area_level_1')) {
          return component.short_name;
        }
      }
    }
    return '';
  }

  function getCountry(
    place: google.maps.places.PlaceResult | undefined
  ): string {
    if (place && place.address_components) {
      for (let component of place.address_components) {
        if (component.types.includes('country')) {
          return component.long_name;
        }
      }
    }
    return '';
  }

  function getPostalCode(
    place: google.maps.places.PlaceResult | undefined
  ): string {
    if (place && place.address_components) {
      for (let component of place.address_components) {
        if (component.types.includes('postal_code')) {
          return component.short_name;
        }
      }
    }
    return '';
  }

  const handleBlur = (e: any) => {
    setIsTouched(true);
  };

  const handleChange = (e: any) => {
    e.preventDefault();
    const inputValue = e.target.value;
    setPlace(inputValue);

    setIsValid(false);
    if (!(inputRef.current === document.activeElement)) setIsTouched(true); // Address was autofilled
    const addressInfo = {
      components: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
      },
      fullAddress: '',
      googleFormattedAddress: '',
      isValid: false,
    };
    onAddressSelected(addressInfo);
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: API_KEY!,
    libraries: ['places'],
  });

  useEffect(() => {
    if (isLoaded) setGoogleAPILoaded(true);
  }, [isLoaded]);

  return (
    <div className="my-4">
      {isLoaded && (
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          options={{
            types: ['address'],
            componentRestrictions: { country: selectedCountry },
          }}>
          <div className="">
            <div
              style={{
                pointerEvents: 'none',
                color: 'gray',
                fontSize: '20px',
              }}></div>
            <label
              className={`block text-[13px] tracking-wide mb-2 ${
                isTouched && !isValid ? 'text-red-500' : 'text-[#141313]'
              }`}>
              ADDRESS <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              className={`w-full rounded-none focus:outline-none ${
                isTouched && !isValid
                  ? 'border-b border-b-[#de2100]'
                  : 'border-none'
              }`}
              type="text"
              required
              placeholder="Address"
              data-variant="custom-value"
              color="gray.700"
              onBlur={handleBlur}
              value={place || ''}
              onChange={handleChange}
              onFocus={() =>
                inputRef.current?.setAttribute('autocomplete', 'one-time-code')
              }
              autoComplete="one-time-code"
              ref={inputRef}
            />
            {isTouched && !isValid && (
              <p className="text-enph-red-functional text-[13px] pt-[5px]">
                Please select your address from the drop-down list
              </p>
            )}
          </div>
        </Autocomplete>
      )}
    </div>
  );
};

export default GoogleAddress;
